import React, { useEffect } from 'react';
import styled from 'styled-components';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions/CardActions';
import ResendVerificationCode from './ResendVerificationCode';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useApiRequest } from '../hooks/useApiRequest';
import { useHistory } from 'react-router-dom';
import { verifyConfirmationCode } from './verifyUserApi';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { faCircleXmark } from '@fortawesome/pro-light-svg-icons';

interface ContentProps {
  status: RequestState<unknown>;
  email: string;
  code?: string;
  onLoginClick: () => unknown;
}

const Content: React.FC<ContentProps> = ({
  status,
  email,
  code,
  onLoginClick,
}) => {
  switch (status.type) {
    case 'REQUEST_START':
      return (
        <div className="text-center">
          <div className="margin-bottom">
            <LoadingSpinner className="spinner" size={25} />
          </div>
          Verifying your account details. Please wait...
        </div>
      );
    case 'REQUEST_SUCCESS':
      return (
        <div className="text-center">
          <div className="margin-bottom">
            <FontAwesomeIcon
              size="2x"
              icon={faCircleCheck}
              className="success"
            />
          </div>
          Account verification completed! Redirecting to the Login...
        </div>
      );
    case 'REQUEST_INIT':
      if (code && email) {
        return null;
      }
    // falls through
    case 'REQUEST_ERROR':
    default:
      return (
        <div className="text-center">
          <div className="margin-bottom">
            <div className="margin-bottom">
              <FontAwesomeIcon
                size="2x"
                icon={faCircleXmark}
                className="error"
              />
            </div>
            Unable to verify the account details. Either the link is expired or
            your account is already verified.
          </div>
          <CardActions className="resend-button">
            <Button color="primary" variant="contained" onClick={onLoginClick}>
              Login
            </Button>
            <ResendVerificationCode email={email} />
          </CardActions>
        </div>
      );
  }
};

const StyledDiv = styled.div`
  .text-center {
    text-align: center;
  }
  .resend-button {
    margin-top: 30px;
    justify-content: center;
  }
  .margin-bottom {
    margin-bottom: 15px;
  }
  .success {
    color: var(--success);
  }
  .error {
    color: var(--critical);
  }
`;

interface VerifyUserContentProps {
  email: string;
  code?: string;
}

const VerifyUserContent: React.FC<VerifyUserContentProps> = ({
  email,
  code,
}) => {
  const { push } = useHistory();
  const [verify, makeVerificationRequest] = useApiRequest<unknown>();
  const onLoginClick = () => push(`/login?email=${email}`);

  useEffect(() => {
    if (verify.type === 'REQUEST_SUCCESS') {
      setTimeout(onLoginClick, 2000);
      return;
    }
    if (verify.type === 'REQUEST_INIT' && code) {
      makeVerificationRequest(verifyConfirmationCode(email, code));
    }
  }, [code, verify.type]);

  return (
    <StyledDiv>
      <Content
        status={verify}
        email={email}
        code={code}
        onLoginClick={onLoginClick}
      />
    </StyledDiv>
  );
};

export default VerifyUserContent;
